import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useEvent } from '@/hooks/useEvent'
import { supabase } from '@/lib/supabaseClient'
import type { EventStatus, HotelRoomInventoryRow, HotelRow, RoomTypeRow } from '@/lib/types'
import { Badge, Button, Card, Checkbox, Field, Input, Select, Textarea, useConfirm, useToast } from '@/components/ui'

const TIMEZONES = ['Africa/Cairo', 'Asia/Dubai', 'Europe/London', 'Europe/Paris', 'America/New_York', 'UTC']

export default function EventSettings() {
  const { event, reload } = useEvent()
  const navigate = useNavigate()
  const { push } = useToast()
  const { confirm, confirmDialog } = useConfirm()

  const [name, setName] = useState(event.name)
  const [code, setCode] = useState(event.code)
  const [startDate, setStartDate] = useState(event.start_date || '')
  const [endDate, setEndDate] = useState(event.end_date || '')
  const [venue, setVenue] = useState(event.venue || '')
  const [city, setCity] = useState(event.city || '')
  const [description, setDescription] = useState(event.description || '')
  const [timezone, setTimezone] = useState(event.timezone)
  const [logoUrl, setLogoUrl] = useState(event.logo_url || '')
  const [accentColor, setAccentColor] = useState(event.accent_color || '#1E3866')
  const [defaultAccessCode, setDefaultAccessCode] = useState(event.default_require_access_code)
  const [defaultFinalSubmission, setDefaultFinalSubmission] = useState(event.default_require_final_submission)
  const [showPhysical, setShowPhysical] = useState(event.show_physical_room_numbers_to_companies)
  const [preventOverallocation, setPreventOverallocation] = useState(event.prevent_overallocation)
  const [duplicatePolicy, setDuplicatePolicy] = useState(event.company_duplicate_guest_policy)
  const [saving, setSaving] = useState(false)

  async function saveGeneral() {
    if (!name.trim() || !code.trim()) {
      push('Event name and code are required', 'error')
      return
    }
    setSaving(true)
    const { error } = await supabase
      .from('events')
      .update({
        name: name.trim(),
        code: code.trim(),
        start_date: startDate || null,
        end_date: endDate || null,
        venue: venue.trim() || null,
        city: city.trim() || null,
        description: description.trim() || null,
        timezone,
        logo_url: logoUrl.trim() || null,
        accent_color: accentColor || null,
      })
      .eq('id', event.id)
    setSaving(false)
    if (error) push(error.message, 'error')
    else {
      push('Saved')
      reload()
    }
  }

  async function savePolicies() {
    const { error } = await supabase
      .from('events')
      .update({
        default_require_access_code: defaultAccessCode,
        default_require_final_submission: defaultFinalSubmission,
        show_physical_room_numbers_to_companies: showPhysical,
        prevent_overallocation: preventOverallocation,
        company_duplicate_guest_policy: duplicatePolicy,
      })
      .eq('id', event.id)
    if (error) push(error.message, 'error')
    else {
      push('Saved')
      reload()
    }
  }

  async function setStatus(status: EventStatus) {
    const { error } = await supabase.from('events').update({ status }).eq('id', event.id)
    if (error) push(error.message, 'error')
    else {
      push(`Event status set to ${status}`)
      reload()
    }
  }

  async function archiveEvent() {
    const ok = await confirm({
      title: `Archive ${event.name}?`,
      description: 'The event disappears from the active list. No data is deleted and it can be restored from the database if needed.',
      tone: 'danger',
      confirmLabel: 'Archive',
    })
    if (!ok) return
    const { error } = await supabase.from('events').update({ status: 'archived', archived_at: new Date().toISOString() }).eq('id', event.id)
    if (error) push(error.message, 'error')
    else {
      push('Event archived')
      navigate('/admin/events')
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <h1 className="mb-6 text-lg font-semibold text-slate-900">Event Settings</h1>

      <div className="space-y-6">
        <Card className="p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">General</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Event Name" required>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label="Event Code" required>
                <Input value={code} onChange={(e) => setCode(e.target.value)} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start Date">
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Field>
              <Field label="End Date">
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Venue">
                <Input value={venue} onChange={(e) => setVenue(e.target.value)} />
              </Field>
              <Field label="City">
                <Input value={city} onChange={(e) => setCity(e.target.value)} />
              </Field>
            </div>
            <Field label="Description">
              <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <Field label="Timezone" hint="Used to interpret deadlines for this event by default.">
              <Select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </Select>
            </Field>
            <Button variant="primary" loading={saving} onClick={saveGeneral}>
              Save
            </Button>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">Branding</h3>
          <p className="mb-3 text-xs text-slate-500">Shown on the company portal header. Logo, name and accent color only — not a page builder.</p>
          <div className="space-y-3">
            <Field label="Logo URL" hint="A hosted image URL. Leave blank for no logo.">
              <Input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://…" />
            </Field>
            <Field label="Accent Color">
              <div className="flex items-center gap-2">
                <input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="h-9 w-14 rounded border border-slate-300" />
                <Input value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="max-w-[120px]" />
              </div>
            </Field>
            <Button variant="primary" loading={saving} onClick={saveGeneral}>
              Save Branding
            </Button>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">Defaults & Policies</h3>
          <div className="space-y-2.5">
            <Checkbox label="Require an access code (PIN) for new company portals by default" checked={defaultAccessCode} onChange={(e) => setDefaultAccessCode(e.target.checked)} />
            <Checkbox label="Require final submission for new companies by default" checked={defaultFinalSubmission} onChange={(e) => setDefaultFinalSubmission(e.target.checked)} />
            <Checkbox label="Show physical room numbers to companies by default" checked={showPhysical} onChange={(e) => setShowPhysical(e.target.checked)} />
            <Checkbox
              label="Block allocations that would exceed a hotel's inventory limit (otherwise just warns)"
              checked={preventOverallocation}
              onChange={(e) => setPreventOverallocation(e.target.checked)}
            />
            <div>
              <span className="mb-1 block text-sm text-slate-700">Duplicate mobile number policy</span>
              <Select className="max-w-xs" value={duplicatePolicy} onChange={(e) => setDuplicatePolicy(e.target.value as 'block' | 'warn')}>
                <option value="block">Block duplicate mobile numbers</option>
                <option value="warn">Warn only, allow admin override</option>
              </Select>
            </div>
            <Button variant="primary" className="mt-1" onClick={savePolicies}>
              Save Policies
            </Button>
          </div>
        </Card>

        <HotelInventoryCard eventId={event.id} />

        <Card className="p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">Event Status</h3>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-sm text-slate-600">Current:</span>
            <Badge tone={event.status === 'active' ? 'green' : event.status === 'closed' ? 'amber' : 'slate'}>{event.status}</Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            {(['draft', 'active', 'closed'] as EventStatus[]).map((s) => (
              <Button key={s} size="sm" variant={event.status === s ? 'primary' : 'secondary'} onClick={() => setStatus(s)}>
                Set {s}
              </Button>
            ))}
          </div>
        </Card>

        <Card className="border-red-200 bg-red-50 p-5">
          <h3 className="mb-2 text-sm font-semibold text-red-900">Archive Event</h3>
          <p className="mb-3 text-sm text-red-700">Soft-deletes the event. All hotels, companies, rooms and guests are preserved but hidden from the active events list.</p>
          <Button variant="danger" onClick={archiveEvent}>
            Archive Event
          </Button>
        </Card>
      </div>
      {confirmDialog}
    </div>
  )
}

function HotelInventoryCard({ eventId }: { eventId: string }) {
  const [hotels, setHotels] = useState<HotelRow[]>([])
  const [roomTypes, setRoomTypes] = useState<RoomTypeRow[]>([])
  const [inventory, setInventory] = useState<HotelRoomInventoryRow[]>([])
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const { push } = useToast()

  async function load() {
    const [{ data: h }, { data: rt }, { data: inv }] = await Promise.all([
      supabase.from('hotels').select('*').eq('event_id', eventId).eq('is_active', true).order('name'),
      supabase.from('room_types').select('*').eq('event_id', eventId).eq('is_active', true).order('name'),
      supabase.from('hotel_room_inventory').select('*').eq('event_id', eventId),
    ])
    setHotels((h as HotelRow[]) || [])
    setRoomTypes((rt as RoomTypeRow[]) || [])
    setInventory((inv as HotelRoomInventoryRow[]) || [])
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  async function saveLimit(hotelId: string, roomTypeId: string, value: string) {
    const key = `${hotelId}:${roomTypeId}`
    const trimmed = value.trim()
    if (trimmed === '') {
      const existing = inventory.find((i) => i.hotel_id === hotelId && i.room_type_id === roomTypeId)
      if (existing) await supabase.from('hotel_room_inventory').delete().eq('id', existing.id)
      load()
      return
    }
    const total = parseInt(trimmed, 10)
    if (!Number.isFinite(total) || total < 0) {
      push('Enter a whole number, or leave blank for no limit', 'error')
      return
    }
    const existing = inventory.find((i) => i.hotel_id === hotelId && i.room_type_id === roomTypeId)
    const { error } = existing
      ? await supabase.from('hotel_room_inventory').update({ total_rooms: total }).eq('id', existing.id)
      : await supabase.from('hotel_room_inventory').insert({ event_id: eventId, hotel_id: hotelId, room_type_id: roomTypeId, total_rooms: total })
    if (error) push(error.message, 'error')
    else {
      setDrafts((d) => {
        const next = { ...d }
        delete next[key]
        return next
      })
      load()
    }
  }

  if (hotels.length === 0 || roomTypes.length === 0) return null

  return (
    <Card className="p-5">
      <h3 className="mb-1 text-sm font-semibold text-slate-900">Hotel Inventory Limits</h3>
      <p className="mb-3 text-xs text-slate-500">
        Optional ceiling on total rooms allocatable per hotel/room type across all companies. Leave blank for no limit. Over-limit allocations warn by default, or are blocked if "Block
        allocations that would exceed inventory" is on above.
      </p>
      <div className="space-y-2">
        {hotels.map((h) =>
          roomTypes.map((rt) => {
            const key = `${h.id}:${rt.id}`
            const existing = inventory.find((i) => i.hotel_id === h.id && i.room_type_id === rt.id)
            const value = drafts[key] ?? (existing ? String(existing.total_rooms) : '')
            return (
              <div key={key} className="flex items-center justify-between gap-3 rounded border border-slate-100 px-3 py-2 text-sm">
                <span className="text-slate-700">
                  {h.name} — {rt.name}
                </span>
                <Input
                  className="h-8 w-24 px-2 py-1 text-sm"
                  placeholder="No limit"
                  value={value}
                  onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                  onBlur={(e) => saveLimit(h.id, rt.id, e.target.value)}
                />
              </div>
            )
          })
        )}
      </div>
    </Card>
  )
}
